import React, { useMemo } from "react";
import { Column } from "components/Layout/Grid";
import Text from "components/Text";

export interface IAnsweredProductSuggestion {
  code?: string;
  description?: string;
  manufacturer?: string;
  unitPurchase?: string;
  quantity?: number;
  suggested?: boolean;
}

export interface IAnsweredDetailItem {
  item?: string | number;
  code?: string;
  description?: string;
  preferedBrands?: string;
  unidade?: string;
  mensagem?: string;
  product?: {
    unitPurchase?: string;
    description?: string;
    code?: string;
  };
  sequenceNumber?: string | number;
  productSuggestion?: IAnsweredProductSuggestion | IAnsweredProductSuggestion[];
  productsSuggestion?: IAnsweredProductSuggestion[];
}

export interface IAnsweredRow {
  id?: string;
  code?: string;
  createdAt?: string;
  customerName?: string;
  razaoSocial?: string;
  category?: string;
  qtd?: number;
  status?: string;
  statusDescription?: string;
  supplierStatus?: string;
  expiresAt?: string;
  origin?: string;
  items?: IAnsweredDetailItem[];
  company?: { crmCustomerCode?: string };
}

const DETAIL_COLUMNS = [
  { key: "item", label: "Item", width: 90 },
  { key: "code", label: "Código", width: 160 },
  { key: "description", label: "Descrição", width: 350 },
  { key: "preferedBrands", label: "Marca", width: 135 },
  { key: "product.unitPurchase", label: "Unidade", width: 260 },
  { key: "mensagem", label: "Mensagem", width: 135 },
] as const;

const gridTemplateColumns = DETAIL_COLUMNS.map((c) => `minmax(0, ${c.width}px)`).join(" ");

function getSuggestionRowsForItem(item: IAnsweredDetailItem): IAnsweredProductSuggestion[] {
  return [item.productSuggestion as IAnsweredProductSuggestion];
}

function getDetailCellValue(item: IAnsweredDetailItem, key: string): string {
  if (key === "item") {
    return String(item?.sequenceNumber ?? item.item ?? "-");
  }
  if (key === "product.unitPurchase") {
    return String(item.product?.unitPurchase ?? item.unidade ?? "-");
  }
  if (key === "description") {
    return String(item.product?.description ?? item.description ?? "-");
  }
  if (key === "code") {
    return String(item.product?.code ?? item.code ?? "-");
  }
  const value = item[key as keyof IAnsweredDetailItem];
  return typeof value === "object" ? "-" : String(value ?? "-");
}

function getSuggestionRowCellValue(suggestion: IAnsweredProductSuggestion, key: string): string {
  if (key === "item") {
    return "De/Para";
  }
  if (key === "code") {
    const c = suggestion.code ?? suggestion.code;
    return c !== undefined && c !== null && String(c).trim() !== "" ? String(c) : "-";
  }
  if (key === "description") {
    const d = suggestion.description;
    return d !== undefined && d !== null && String(d).trim() !== "" ? String(d) : "-";
  }
  if (key === "preferedBrands") {
    const b = suggestion.manufacturer;
    return b !== undefined && b !== null && String(b).trim() !== "" ? String(b) : "-";
  }
  if (key === "product.unitPurchase") {
    const u = suggestion.unitPurchase;
    return u !== undefined && u !== null && String(u).trim() !== "" ? String(u) : "-";
  }
  if (key === "mensagem") {
    return "-";
  }
  return "-";
}

type DetailGridRow =
  | { kind: "item"; item: IAnsweredDetailItem }
  | { kind: "suggestion"; suggestion: IAnsweredProductSuggestion };

function buildDetailGridRows(items: IAnsweredDetailItem[]): DetailGridRow[] {
  const rows: DetailGridRow[] = [];
  for (const item of items) {
    rows.push({ kind: "item", item });
    for (const suggestion of getSuggestionRowsForItem(item)) {
      rows.push({ kind: "suggestion", suggestion });
    }
  }
  return rows;
}

function attachDeParaAwareStripe(
  gridRows: DetailGridRow[]
): Array<{ rowData: DetailGridRow; backgroundColor: string }> {
  let band = 0;
  const STRIPE_EVEN = "#ffffff";
  const STRIPE_ODD = "#F5F7F8";
  const result: Array<{ rowData: DetailGridRow; backgroundColor: string }> = [];

  for (let idx = 0; idx < gridRows.length; idx++) {
    const rowData = gridRows[idx];
    const itemLabel =
      rowData.kind === "item"
        ? getDetailCellValue(rowData.item, "item")
        : getSuggestionRowCellValue(rowData.suggestion, "item");

    result.push({
      rowData,
      backgroundColor: band % 2 === 0 ? STRIPE_EVEN : STRIPE_ODD,
    });

    if (itemLabel === "De/Para") {
      band++;
    } else if (rowData.kind === "item") {
      const next = gridRows[idx + 1];
      if (!next || next.kind === "item") {
        band++;
      }
    }
  }

  return result;
}

export const AnsweredItemsDetailDropdown: React.FC<IAnsweredRow> = (row) => {
  const detailItems = row.items ?? [];
  const gridRowsWithStripe = useMemo(() => {
    const gridRows = buildDetailGridRows(row.items ?? []);
    return attachDeParaAwareStripe(gridRows);
  }, [row.items]);

  return (
    <Column
      className="animated fadeIn bg-white py-3 unanswered-items-detail-dropdown"
      style={{
        marginLeft: 0,
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        paddingLeft: 20,
        paddingRight: 20,
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <Text className="font-bold color-blue-gray mt-2 mb-3" size={12}>
        {detailItems.length > 0 ? "" : "Sem itens para exibir"}
      </Text>
      {detailItems.length > 0 && (
        <div className="unanswered-detail-grid-wrapper" style={{ overflowX: "auto", maxWidth: "99%", minWidth: 0 }}>
          <div className="unanswered-detail-grid" style={{ width: "100%", minWidth: 0 }}>
            <div
              className="unanswered-detail-grid-row unanswered-detail-grid-header"
              style={{ display: "grid", gridTemplateColumns }}
            >
              {DETAIL_COLUMNS.map((col, colIdx) => (
                <div
                  key={col.key}
                  className="d-flex align-center py-2 px-2 text-left font-bold"
                  style={{
                    minWidth: 0,
                    backgroundColor: "#D0DEE1",
                    color: "#2d3748",
                    fontSize: 12,
                    borderTopLeftRadius: colIdx === 0 ? 8 : 0,
                    borderTopRightRadius: colIdx === DETAIL_COLUMNS.length - 1 ? 8 : 0,
                  }}
                >
                  {col.label}
                </div>
              ))}
            </div>
            {gridRowsWithStripe.map(({ rowData, backgroundColor }, idx) => {
              const isSuggestion = rowData.kind === "suggestion";

              return (
                <div
                  key={idx}
                  className="unanswered-detail-grid-row"
                  style={{
                    display: "grid",
                    gridTemplateColumns,
                    minHeight: 50,
                    alignItems: "start",
                    backgroundColor,
                  }}
                >
                  {DETAIL_COLUMNS.map((col) => (
                    <div
                      key={col.key}
                      className={`color-blue-gray text-left py-2 px-2${
                        col.key === "description" || col.key === "preferedBrands" ? " unanswered-detail-grid-cell--wrap" : ""
                      }`}
                      style={{
                        minWidth: 0,
                        maxWidth: "100%",
                        fontSize: 12,
                        ...(col.key === "description" || col.key === "preferedBrands"
                          ? {}
                          : {
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }),
                        fontStyle: isSuggestion && col.key === "item" ? "italic" : undefined,
                      }}
                    >
                      {rowData.kind === "item"
                        ? getDetailCellValue(rowData.item, col.key)
                        : getSuggestionRowCellValue(rowData.suggestion, col.key)}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Column>
  );
};
